import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const User = () => {
  const navigate = useNavigate();
  const { token, user, updateUser, API_URL, currentLang, getCsrfToken } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    lang: 'ru',
    current_password: '',
    new_password: '',
    confirm_password: '',
    role_id: 0
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  // Состояние для показа/скрытия паролей
  const [showPasswords, setShowPasswords] = useState({
    current_password: false,
    new_password: false,
    confirm_password: false
  });

  // Инициализация формы данными пользователя
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        lang: user.lang || 'ru',
        role_id: user.role_id || 0
      }));
    }
  }, [user]);

  const validateForm = (section) => {
    const newErrors = {};
    
    if (section === 'profile' || section === 'all') {
      if (!formData.name.trim()) {
        newErrors.name = 'Имя обязательно';
      }
      
      if (!formData.email) {
        newErrors.email = 'Email обязателен';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Введите корректный email';
      }
    }
    
    if (section === 'password' || section === 'all') {
      if (formData.new_password && !formData.current_password) {
        newErrors.current_password = 'Текущий пароль обязателен для смены пароля';
      }
      
      if (formData.new_password && formData.new_password.length < 6) {
        newErrors.new_password = 'Новый пароль должен содержать минимум 6 символов';
      }
      
      if (formData.new_password && formData.new_password !== formData.confirm_password) {
        newErrors.confirm_password = 'Пароли не совпадают';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Очищаем ошибку поля при вводе
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Очищаем сообщение об успехе при изменении данных
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm('all')) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const requestData = {
        name: formData.name,
        email: formData.email,
        lang: formData.lang
      };

      // Добавляем пароли только если они заполнены
      if (formData.current_password && formData.new_password) {
        requestData.current_password = formData.current_password;
        requestData.new_password = formData.new_password;
      }

      const response = await fetch(`${API_URL}${currentLang}/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': currentCsrfToken
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Данные успешно обновлены!');
        
        // Очищаем поля паролей
        setFormData(prev => ({
          ...prev,
          current_password: '',
          new_password: '',
          confirm_password: ''
        }));
        
        // Обновляем данные пользователя в контексте
        if (updateUser) {
          updateUser({
            ...user,
            name: formData.name,
            email: formData.email,
            lang: formData.lang,
            role_id: formData.role_id
          });
        }
      } else {
        setErrors({ submit: data.message || 'Ошибка при обновлении данных' });
      }
    } catch (error) {
      setErrors({ submit: 'Ошибка сети: ' + error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const languages = [
    { code: 'ru', name: 'Русский' },
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' }
  ];
  
  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Заголовок */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white break-words">
                  {currentLang === 'ru' ? 'Профиль пользователя' : 'User Profile'}
                </h1>
                <p className="text-blue-100 mt-1 sm:mt-2 text-sm sm:text-base">
                  {currentLang === 'ru' ? 'Управление личными данными и настройками' : 'Manage personal data and settings'}
                </p>
              </div>
              <button
                onClick={() => navigate('/seo/dashboard')}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 sm:px-4 py-2 rounded-md transition-all duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base w-full sm:w-auto"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>{currentLang === 'ru' ? 'На главную' : 'Home'}</span>
              </button>
            </div>
          </div>

          {/* Навигация по вкладкам */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4 sm:space-x-8 px-3 sm:px-4 lg:px-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {currentLang === 'ru' ? 'Основная информация' : 'Basic Info'}
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === 'password'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {currentLang === 'ru' ? 'Смена пароля' : 'Password'}
              </button>
            </nav>
          </div>

          {/* Содержимое вкладок */}
          <div className="p-3 sm:p-4 lg:p-6">
            {activeTab === 'profile' && (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Имя */}
                  <div>
                    <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {currentLang === 'ru' ? 'Имя' : 'Name'}
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full px-2 sm:px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm ${
                        errors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={currentLang === 'ru' ? 'Введите ваше имя' : 'Enter your name'}
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-2 sm:px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  {/* Язык */}
                  <div>
                    <label htmlFor="lang" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {currentLang === 'ru' ? 'Язык по умолчанию' : 'Default Language'}
                    </label>
                    <select
                      id="lang"
                      value={formData.lang}
                      onChange={(e) => handleInputChange('lang', e.target.value)}
                      className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
                    >
                      {languages.map(lang => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Роль (только для просмотра) */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {currentLang === 'ru' ? 'Роль' : 'Role'}
                    </label>
                    <div className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        user?.role === 'admin' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user?.role || 'user'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Кнопка сохранения */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                  >
                    {isLoading ? (currentLang === 'ru' ? 'Сохранение...' : 'Saving...') : (currentLang === 'ru' ? 'Сохранить изменения' : 'Save Changes')}
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Текущий пароль */}
                  <div>
                    <label htmlFor="current_password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {currentLang === 'ru' ? 'Текущий пароль' : 'Current Password'}
                    </label>
                    <div className="relative">
                    <input
                        type={showPasswords.current_password ? "text" : "password"}
                      id="current_password"
                      value={formData.current_password}
                      onChange={(e) => handleInputChange('current_password', e.target.value)}
                        className={`w-full px-2 sm:px-3 py-2 pr-8 sm:pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm ${
                        errors.current_password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={currentLang === 'ru' ? 'Введите текущий пароль' : 'Enter current password'}
                    />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current_password')}
                        className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.current_password ? (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.current_password && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.current_password}</p>
                    )}
                  </div>

                  {/* Новый пароль */}
                  <div>
                    <label htmlFor="new_password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {currentLang === 'ru' ? 'Новый пароль' : 'New Password'}
                    </label>
                    <div className="relative">
                    <input
                        type={showPasswords.new_password ? "text" : "password"}
                      id="new_password"
                      value={formData.new_password}
                      onChange={(e) => handleInputChange('new_password', e.target.value)}
                        className={`w-full px-2 sm:px-3 py-2 pr-8 sm:pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm ${
                        errors.new_password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={currentLang === 'ru' ? 'Введите новый пароль' : 'Enter new password'}
                    />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new_password')}
                        className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.new_password ? (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.new_password && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.new_password}</p>
                    )}
                  </div>

                  {/* Подтверждение пароля */}
                  <div className="lg:col-span-2">
                    <label htmlFor="confirm_password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {currentLang === 'ru' ? 'Подтвердите новый пароль' : 'Confirm New Password'}
                    </label>
                    <div className="relative">
                    <input
                        type={showPasswords.confirm_password ? "text" : "password"}
                      id="confirm_password"
                      value={formData.confirm_password}
                      onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                        className={`w-full px-2 sm:px-3 py-2 pr-8 sm:pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm ${
                        errors.confirm_password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={currentLang === 'ru' ? 'Повторите новый пароль' : 'Repeat new password'}
                    />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm_password')}
                        className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.confirm_password ? (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.confirm_password && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.confirm_password}</p>
                    )}
                  </div>
                </div>

                {/* Кнопка сохранения */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoading || (!formData.current_password && !formData.new_password)}
                    className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                  >
                    {isLoading ? (currentLang === 'ru' ? 'Сохранение...' : 'Saving...') : (currentLang === 'ru' ? 'Изменить пароль' : 'Change Password')}
                  </button>
                </div>
              </form>
            )}

            {/* Сообщения об ошибках и успехе */}
            {errors.submit && (
              <div className="mt-4 p-3 sm:p-4 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
                {errors.submit}
              </div>
            )}

            {successMessage && (
              <div className="mt-4 p-3 sm:p-4 bg-green-100 border border-green-400 text-green-700 rounded-md text-sm">
                {successMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default User;
